import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import {
  Home, Layers, Sparkles, Cpu, Server, Receipt, BookOpen, Users, Trophy,
  LayoutDashboard, FileText, ShieldCheck, BarChart3, Mail, LogIn, LogOut,
  HelpCircle, MessageSquare, Activity,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * Global Cmd+K command palette — invoked anywhere with ⌘K / Ctrl+K.
 * Surfaces navigation, role-scoped destinations and quick actions.
 */
const PUBLIC = [
  { id: "home",       label: "Home",                    path: "/",            Icon: Home,        group: "Navigate" },
  { id: "solutions",  label: "Solutions",               path: "/solutions",   Icon: Layers,      group: "Navigate" },
  { id: "sectors",    label: "Sectors",                 path: "/sectors",     Icon: BookOpen,    group: "Navigate" },
  { id: "ai",         label: "AI Systems",              path: "/ai-systems",  Icon: Sparkles,    group: "Navigate" },
  { id: "infra",      label: "Infrastructure",          path: "/enterprise",  Icon: Server,      group: "Navigate" },
  { id: "pricing",    label: "Pricing",                 path: "/pricing",     Icon: Receipt,     group: "Navigate" },
  { id: "why",        label: "Why Connexis (vs competitors)", path: "/why-connexis", Icon: Sparkles, group: "Navigate" },
  { id: "partners",   label: "Partner programme (refer buyers)", path: "/partners",     Icon: Users,       group: "Navigate" },
  { id: "publisher-apply", label: "Sell your leads (publisher apply)", path: "/publisher/apply", Icon: BarChart3, group: "Navigate" },
  { id: "leaders",    label: "Partner leaderboard",     path: "/leaderboard", Icon: Trophy,      group: "Navigate" },
  { id: "activity",   label: "Live activity feed",      path: "/activity",    Icon: Activity,    group: "Navigate" },
  { id: "docs",       label: "Docs",                    path: "/docs",        Icon: FileText,    group: "Resources" },
  { id: "status",     label: "Status page",             path: "/status",      Icon: ShieldCheck, group: "Resources" },
  { id: "contact",    label: "Contact",                 path: "/contact",     Icon: Mail,        group: "Resources" },
  { id: "demo",       label: "Book a demo",             path: "/book-demo",   Icon: MessageSquare,group: "Resources" },
];

const CLIENT = [
  { id: "c-overview", label: "Dashboard overview",      path: "/dashboard",                Icon: LayoutDashboard, group: "Client" },
  { id: "c-leads",    label: "Leads",                   path: "/dashboard/leads",          Icon: BarChart3,       group: "Client" },
  { id: "c-ai",       label: "My AI systems",           path: "/dashboard/ai-systems",     Icon: Cpu,             group: "Client" },
  { id: "c-billing",  label: "Billing",                 path: "/dashboard/billing",        Icon: Receipt,         group: "Client" },
  { id: "c-support",  label: "Support",                 path: "/dashboard/support",        Icon: HelpCircle,      group: "Client" },
];

const ADMIN = [
  { id: "a-overview",   label: "Admin overview",        path: "/admin",                  Icon: LayoutDashboard, group: "Admin" },
  { id: "a-routing",    label: "Routing / Ping Trees",  path: "/admin/routing",          Icon: Server,          group: "Admin" },
  { id: "a-buyers",     label: "Buyers",                path: "/admin/buyers",           Icon: Users,           group: "Admin" },
  { id: "a-campaigns",  label: "Campaigns & reservations", path: "/admin/campaigns",     Icon: Receipt,         group: "Admin" },
  { id: "a-compliance", label: "Compliance",            path: "/admin/compliance",       Icon: ShieldCheck,     group: "Admin" },
  { id: "a-users",      label: "Users",                 path: "/admin/users",            Icon: Users,           group: "Admin" },
  { id: "a-feedback",   label: "Feedback inbox",        path: "/admin/feedback",         Icon: MessageSquare,   group: "Admin" },
  { id: "a-partners",   label: "Partner applications",  path: "/admin/partners",         Icon: Trophy,          group: "Admin" },
  { id: "a-publishers", label: "Publishers",            path: "/admin/publishers",       Icon: BarChart3,       group: "Admin" },
  { id: "a-invoices",   label: "Invoices",              path: "/admin/invoices",         Icon: Receipt,         group: "Admin" },
];

const PUBLISHER = [
  { id: "p-overview",   label: "Publisher dashboard",   path: "/publisher",             Icon: LayoutDashboard, group: "Publisher" },
  { id: "p-settings",   label: "Account settings",      path: "/publisher/settings",    Icon: ShieldCheck,     group: "Publisher" },
  { id: "p-apply",      label: "Recruit another publisher",  path: "/publisher/apply",   Icon: BarChart3,       group: "Publisher" },
];

const AFFILIATE = [
  { id: "af-overview",    label: "Affiliate overview",  path: "/affiliate",             Icon: LayoutDashboard, group: "Affiliate" },
  { id: "af-links",       label: "Tracking links",      path: "/affiliate/links",       Icon: Sparkles,        group: "Affiliate" },
  { id: "af-commissions", label: "Commissions",         path: "/affiliate/commissions", Icon: Receipt,         group: "Affiliate" },
  { id: "af-creatives",   label: "Creatives",           path: "/affiliate/creatives",   Icon: FileText,        group: "Affiliate" },
  { id: "af-settings",    label: "Account settings",    path: "/affiliate/settings",    Icon: ShieldCheck,     group: "Affiliate" },
];

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Keyboard shortcut
  useEffect(() => {
    const onKey = (e) => {
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const go = useCallback((path) => {
    setOpen(false);
    navigate(path);
  }, [navigate]);

  // Compose items by role
  let items = [...PUBLIC];
  if (user?.role === "client") items = [...CLIENT, ...PUBLIC];
  if (user?.role === "admin") items = [...ADMIN, ...PUBLIC];
  if (user?.role === "affiliate") items = [...AFFILIATE, ...PUBLIC];
  if (user?.role === "publisher") items = [...PUBLISHER, ...PUBLIC];

  // Group items
  const groups = items.reduce((acc, item) => {
    (acc[item.group] = acc[item.group] || []).push(item);
    return acc;
  }, {});

  return (
    <CommandDialog open={open} onOpenChange={setOpen} data-testid="cmdk-palette">
      <CommandInput placeholder="Type a command or search…" />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        {Object.entries(groups).map(([group, list], gi) => (
          <React.Fragment key={group}>
            {gi > 0 && <CommandSeparator />}
            <CommandGroup heading={group}>
              {list.map((it) => (
                <CommandItem key={it.id} onSelect={() => go(it.path)} data-testid={`cmdk-${it.id}`}>
                  <it.Icon className="mr-2 size-4 text-slate-500" strokeWidth={1.6} />
                  <span>{it.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </React.Fragment>
        ))}

        {/* Auth actions */}
        <CommandSeparator />
        <CommandGroup heading="Account">
          {user ? (
            <CommandItem onSelect={() => { setOpen(false); logout(); }} data-testid="cmdk-logout">
              <LogOut className="mr-2 size-4 text-slate-500" strokeWidth={1.6} />
              <span>Sign out</span>
              <CommandShortcut>{user.email}</CommandShortcut>
            </CommandItem>
          ) : (
            <CommandItem onSelect={() => go("/login")} data-testid="cmdk-login">
              <LogIn className="mr-2 size-4 text-slate-500" strokeWidth={1.6} />
              <span>Sign in</span>
            </CommandItem>
          )}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
